import type { PanelSide } from "../../core/config/public-config.types.ts";

const fallbackPanelSide: PanelSide = "right";
const panelSides = new Set<PanelSide>(["left", "right"]);

export type WidgetPanel = {
  activeStatus: HTMLElement;
  body: HTMLElement;
  closeButton: HTMLButtonElement;
  element: HTMLElement;
  resetButton: HTMLButtonElement;
  setActivePreferenceCount: (count: number) => void;
  status: HTMLElement;
};

export type CreatePanelInput = {
  activePreferenceCount?: number;
  bodyContent?: HTMLElement;
  document?: Document;
  onClose: () => void;
  onReset?: () => boolean | void;
  side: unknown;
};

export function normalizePanelSide(value: unknown): PanelSide {
  return panelSides.has(value as PanelSide)
    ? (value as PanelSide)
    : fallbackPanelSide;
}

function createButton(document: Document, label: string) {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = label;
  return button;
}

function formatActivePreferenceCount(count: number) {
  return `${Math.max(0, Math.round(count))} ayar aktif`;
}

export function createPanel({
  activePreferenceCount = 0,
  bodyContent,
  document = globalThis.document,
  onClose,
  onReset,
  side,
}: CreatePanelInput): WidgetPanel {
  const normalizedSide = normalizePanelSide(side);
  const panel = document.createElement("section");
  const header = document.createElement("div");
  const headingGroup = document.createElement("div");
  const subtitle = document.createElement("p");
  const actions = document.createElement("div");
  const activeStatus = document.createElement("span");
  const title = document.createElement("h2");
  const body = document.createElement("div");
  const closeButton = createButton(document, "Paneli kapat");
  const resetButton = createButton(document, "Tüm ayarları sıfırla");
  const status = document.createElement("p");

  panel.className = "migrosone-a11y-widget-panel";
  panel.dataset.side = normalizedSide;
  panel.hidden = true;
  panel.setAttribute("aria-label", "Erişilebilirlik ayarları");
  panel.setAttribute("role", "dialog");

  header.className = "migrosone-a11y-widget-panel__header";
  headingGroup.className = "migrosone-a11y-widget-panel__heading";
  title.className = "migrosone-a11y-widget-panel__title";
  title.textContent = "Erişilebilirlik ayarları";
  subtitle.className = "migrosone-a11y-widget-panel__subtitle";
  subtitle.textContent = "Tüm özellikler kategori altında hızlıca düzenlenir.";

  actions.className = "migrosone-a11y-widget-panel__actions";
  activeStatus.className = "migrosone-a11y-widget-panel__active-status";
  activeStatus.textContent = formatActivePreferenceCount(activePreferenceCount);

  closeButton.className = "migrosone-a11y-widget-panel__close";
  closeButton.setAttribute("aria-label", "Paneli kapat");
  closeButton.textContent = "×";
  closeButton.addEventListener("click", onClose);

  resetButton.className = "migrosone-a11y-widget-panel__reset";
  resetButton.setAttribute("aria-label", "Tüm ayarları sıfırla");
  resetButton.addEventListener("click", () => {
    const resetSucceeded = onReset?.() !== false;
    status.textContent = resetSucceeded
      ? "Ayarlar sıfırlandı."
      : "Ayarlar sıfırlanamadı.";
  });

  status.className = "migrosone-a11y-widget-panel__status";
  status.setAttribute("aria-live", "polite");
  status.setAttribute("role", "status");

  body.className = "migrosone-a11y-widget-panel__body";
  if (bodyContent) {
    body.append(bodyContent);
  } else {
    body.textContent = "Kullanılabilir kontroller hazırlanıyor.";
  }

  headingGroup.append(title, subtitle);
  actions.append(activeStatus, resetButton);
  header.append(headingGroup, closeButton, actions);
  panel.append(header, body, status);

  return {
    activeStatus,
    body,
    closeButton,
    element: panel,
    resetButton,
    setActivePreferenceCount(count) {
      activeStatus.textContent = formatActivePreferenceCount(count);
    },
    status,
  };
}

export function setPanelOpen({
  initialFocus,
  launcher,
  open,
  panel,
}: {
  initialFocus: HTMLElement;
  launcher: HTMLElement;
  open: boolean;
  panel: HTMLElement;
}) {
  panel.hidden = !open;
  launcher.setAttribute("aria-expanded", String(open));

  const focusTarget = open ? initialFocus : launcher;
  try {
    focusTarget.focus({ preventScroll: true });
  } catch {
    focusTarget.focus();
  }
}
